import { useMemo, useState } from 'react';

import { FileItem, FolderItem, PostListItem } from 'api';

export const DATE_PRESET_LABELS: Record<string, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7days: 'Last 7 days',
  last30days: 'Last 30 days',
  thisyear: 'This year',
  lastyear: 'Last year',
  custom: 'Custom range',
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function buildDateRanges() {
  const now = new Date();
  const start: Record<string, Date> = {
    today: startOfDay(now),
    yesterday: (() => {
      const d = startOfDay(now);
      d.setDate(d.getDate() - 1);
      return d;
    })(),
    last7days: (() => {
      const d = startOfDay(now);
      d.setDate(d.getDate() - 6);
      return d;
    })(),
    last30days: (() => {
      const d = startOfDay(now);
      d.setDate(d.getDate() - 29);
      return d;
    })(),
    thisyear: startOfDay(new Date(now.getFullYear(), 0, 1)),
    lastyear: startOfDay(new Date(now.getFullYear() - 1, 0, 1)),
  };
  const end: Record<string, Date> = { yesterday: startOfDay(now) };
  return { start, end };
}

export const FILE_TYPE_GROUP_EXTENSIONS: Record<string, string[]> = {
  Word: ['doc', 'docx'],
  Excel: ['xls', 'xlsx', 'csv'],
  PowerPoint: ['ppt', 'pptx'],
  PDF: ['pdf'],
  Text: ['txt'],
  Media: [
    'png',
    'gif',
    'jpeg',
    'jpg',
    'mp4',
    'mkv',
    'mov',
    'avi',
    'flv',
    'mpeg',
    'mpg',
    'webm',
    '3gpp',
    '3gp',
  ],
};

function applyFilterAndSort<T extends { created_at: string }>(
  items: T[],
  opts: {
    search: string;
    getTitle: (item: T) => string;
    createdById: string;
    getAuthorId: (item: T) => string | undefined;
    dateSort: string;
    dateRangeStart: Record<string, Date>;
    dateRangeEnd: Record<string, Date>;
    customStartDate: Date | null;
    customEndDate: Date | null;
    sortOrder: 'none' | 'asc' | 'desc';
    fileExtensions?: string[];
    getExtension?: (item: T) => string | undefined;
    itemTypeFilter?: (item: T) => boolean;
  },
): T[] {
  const {
    search,
    getTitle,
    createdById,
    getAuthorId,
    dateSort,
    dateRangeStart,
    dateRangeEnd,
    customStartDate,
    customEndDate,
    sortOrder,
    fileExtensions,
    getExtension,
    itemTypeFilter,
  } = opts;

  const filtered = items.filter((item) => {
    if (search && !(getTitle(item) ?? '').toLowerCase().includes(search.toLowerCase()))
      return false;
    if (createdById && getAuthorId(item) !== createdById) return false;
    if (itemTypeFilter && !itemTypeFilter(item)) return false;
    if (fileExtensions && fileExtensions.length > 0 && getExtension) {
      if (!fileExtensions.includes(getExtension(item) ?? '')) return false;
    }
    if (dateSort === 'custom') {
      const createdAt = new Date(item.created_at);
      if (customStartDate && createdAt < customStartDate) return false;
      if (customEndDate) {
        const endOfDay = new Date(customEndDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (createdAt > endOfDay) return false;
      }
    } else if (dateSort) {
      const createdAt = new Date(item.created_at);
      const rangeStart = dateRangeStart[dateSort];
      const rangeEnd = dateRangeEnd[dateSort];
      if (rangeStart && createdAt < rangeStart) return false;
      if (rangeEnd && createdAt >= rangeEnd) return false;
    }
    return true;
  });

  if (sortOrder === 'none') return filtered;

  return [...filtered].sort((a, b) => {
    const cmp = (getTitle(a) ?? '').localeCompare(getTitle(b) ?? '');
    return sortOrder === 'asc' ? cmp : -cmp;
  });
}

interface UseDocumentFiltersParams {
  files: FileItem[];
  folders: FolderItem[];
  documents: PostListItem[];
}

export const useDocumentFilters = ({ files, folders, documents }: UseDocumentFiltersParams) => {
  const [search, setSearch] = useState('');
  const [createdById, setCreatedById] = useState('');
  const [dateSort, setDateSort] = useState('');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [sortOrder, setSortOrder] = useState<'none' | 'asc' | 'desc'>('none');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedType, setSelectedType] = useState('');

  const toggleSort = () => {
    setSortOrder((prev) => (prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none'));
  };

  const uniquePostCreators = useMemo(
    () =>
      documents.reduce((acc, doc) => {
        const seen = new Set(acc.map((c) => c.id));
        if (doc.author.id && !seen.has(doc.author.id))
          acc.push(doc.author as { id: string; first_name: string; last_name: string });
        return acc;
      }, [] as { id: string; first_name: string; last_name: string }[]),
    [documents],
  );

  const uniqueFileCreators = useMemo(
    () =>
      files.reduce((acc, file) => {
        const seen = new Set(acc.map((c) => c.id));
        if (file?.created_by?.id && !seen.has(file.created_by.id))
          acc.push(file.created_by as { id: string; first_name: string; last_name: string });
        return acc;
      }, [] as { id: string; first_name: string; last_name: string }[]),
    [files],
  );

  const uniqueFileTypes = useMemo(
    () =>
      files.reduce((acc, file) => {
        const seen = new Set(acc);
        if (file?.file_extension && !seen.has(file.file_extension)) acc.push(file.file_extension);
        return acc;
      }, [] as string[]),
    [files],
  );

  const selectedCreator = uniquePostCreators.find((c) => c.id === createdById);
  const selectedCreatorName = selectedCreator
    ? `${selectedCreator.first_name} ${selectedCreator.last_name}`
    : '';

  const selectedFileCreator = uniqueFileCreators.find((c) => c.id === createdById);
  const selectedFileCreatorName = selectedFileCreator
    ? `${selectedFileCreator.first_name} ${selectedFileCreator?.last_name}`
    : '';

  const { start: dateRangeStart, end: dateRangeEnd } = useMemo(buildDateRanges, []);

  const filterOpts = {
    search,
    createdById,
    dateSort,
    dateRangeStart,
    dateRangeEnd,
    customStartDate,
    customEndDate,
    sortOrder,
  };

  const filteredFiles = useMemo<FileItem[]>(
    () =>
      applyFilterAndSort(files, {
        ...filterOpts,
        getTitle: (f) => f.display_name,
        getAuthorId: (f) => f.created_by?.id,
        fileExtensions:
          selectedType && selectedType !== 'Folders' && selectedType !== 'Media'
            ? FILE_TYPE_GROUP_EXTENSIONS[selectedType] ?? []
            : [],
        getExtension: (f) => f.file_extension.split('.').pop()?.toLowerCase() ?? '',
        itemTypeFilter:
          selectedType === 'Media' ? (f) => f.type === 'image' || f.type === 'video' : undefined,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      files,
      search,
      createdById,
      dateSort,
      customStartDate,
      customEndDate,
      dateRangeStart,
      dateRangeEnd,
      sortOrder,
      selectedType,
    ],
  );

  const filteredFolders = useMemo<FolderItem[]>(
    () =>
      applyFilterAndSort(folders, {
        ...filterOpts,
        getTitle: (f) => f.name,
        getAuthorId: (f) => f.created_by?.id,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      folders,
      search,
      createdById,
      dateSort,
      customStartDate,
      customEndDate,
      dateRangeStart,
      dateRangeEnd,
      sortOrder,
    ],
  );

  const filteredDocuments = useMemo<PostListItem[]>(
    () =>
      applyFilterAndSort(documents, {
        ...filterOpts,
        getTitle: (d) => d.title,
        getAuthorId: (d) => d.author?.id,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      documents,
      search,
      createdById,
      dateSort,
      customStartDate,
      customEndDate,
      dateRangeStart,
      dateRangeEnd,
      sortOrder,
    ],
  );

  return {
    search,
    setSearch,
    createdById,
    setCreatedById,
    dateSort,
    setDateSort,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    sortOrder,
    setSortOrder,
    viewMode,
    setViewMode,
    toggleSort,
    uniqueFileCreators,
    uniquePostCreators,
    uniqueFileTypes,
    selectedCreatorName,
    selectedFileCreatorName,
    selectedType,
    setSelectedType,
    filteredFiles,
    filteredFolders,
    filteredDocuments,
  };
};
